export type MemberCountryCode = 'GB' | 'IE' | 'AU' | 'NZ' | 'CA' | 'US';

export type MemberLocationLike = {
  postcode?: string | null;
  country_code?: string | null;
  display_name?: string | null;
};

export const DEFAULT_MEMBER_COUNTRY_CODE: MemberCountryCode = 'GB';
export const MEMBER_COUNTRY_CODES = ['GB', 'IE', 'AU', 'NZ', 'CA', 'US'] as const;

export const MEMBER_COUNTRY_OPTIONS: Array<{ value: MemberCountryCode; label: string }> = [
  { value: 'GB', label: 'UK' },
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'US', label: 'USA' },
];

const PAUSED_MEMBER_COUNTRY_OPTIONS: Array<{ value: MemberCountryCode; label: string }> = [
  { value: 'IE', label: 'Ireland (paused)' },
  { value: 'CA', label: 'Canada (paused)' },
];

const UK_POSTCODE_PATTERN = /^([A-Z]{1,2}\d[A-Z\d]?)\s?(\d[A-Z]{2})$/i;
const IE_ROUTING_KEY_PATTERN = /^(?:[AC-FHKNPRTV-Y]\d{2}|D6W)$/i;
const IE_EIRCODE_PATTERN = /^(?:(?:[AC-FHKNPRTV-Y]\d{2})|D6W)\s?([0-9AC-FHKNPRTV-Y]{4})$/i;
const CA_POSTAL_CODE_PATTERN = /^([ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z])\s?(\d[ABCEGHJ-NPRSTV-Z]\d)$/i;
const US_ZIP_CODE_PATTERN = /^(\d{5})(?:[-\s]?(\d{4}))?$/;
const AU_NZ_POSTCODE_PATTERN = /^(\d{4})$/;
const MEMBER_COUNTRY_CODE_SET = new Set<string>(MEMBER_COUNTRY_CODES);

const normalizeRawLocationCode = (value: string | null | undefined) =>
  (value ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, ' ');

export const isMemberCountryCode = (
  value: string | null | undefined,
): value is MemberCountryCode => Boolean(value && MEMBER_COUNTRY_CODE_SET.has(value));

export const normalizeMemberCountryCode = (
  value: string | null | undefined,
): MemberCountryCode => (isMemberCountryCode(value) ? value : 'GB');

export const isPausedMemberCountryCode = (
  value: string | null | undefined,
) => {
  const normalized = normalizeMemberCountryCode(value);
  return normalized === 'CA' || normalized === 'IE';
};

export const getMemberCountryPickerOptions = (currentCountryCode?: string | null) => {
  if (isPausedMemberCountryCode(currentCountryCode)) {
    return [...MEMBER_COUNTRY_OPTIONS, ...PAUSED_MEMBER_COUNTRY_OPTIONS];
  }

  return MEMBER_COUNTRY_OPTIONS;
};

export const getMemberCountryLabel = (countryCode: string | null | undefined) => {
  switch (normalizeMemberCountryCode(countryCode)) {
    case 'IE':
      return 'Ireland';
    case 'AU':
      return 'Australia';
    case 'NZ':
      return 'New Zealand';
    case 'CA':
      return 'Canada';
    case 'US':
      return 'United States';
    case 'GB':
    default:
      return 'UK';
  }
};

export const getMemberPostalCodeLabel = (countryCode: string | null | undefined) => {
  switch (normalizeMemberCountryCode(countryCode)) {
    case 'IE':
      return 'Eircode';
    case 'CA':
      return 'Postal code';
    case 'US':
      return 'ZIP code';
    case 'AU':
    case 'NZ':
    case 'GB':
    default:
      return 'Postcode';
  }
};

export const getMemberPostalCodeExample = (countryCode: string | null | undefined) => {
  switch (normalizeMemberCountryCode(countryCode)) {
    case 'IE':
      return 'D02 X285';
    case 'AU':
      return '2000';
    case 'NZ':
      return '6011';
    case 'CA':
      return 'M5V 3L9';
    case 'US':
      return '10001';
    case 'GB':
    default:
      return 'SW1A 1AA';
  }
};

export const getMemberPostalCodeValidationMessage = (countryCode: string | null | undefined) => {
  switch (normalizeMemberCountryCode(countryCode)) {
    case 'IE':
      return 'Please enter a valid Irish Eircode.';
    case 'AU':
      return 'Please enter a valid Australian postcode.';
    case 'NZ':
      return 'Please enter a valid New Zealand postcode.';
    case 'CA':
      return 'Please enter a valid Canadian postal code.';
    case 'US':
      return 'Please enter a valid US ZIP code.';
    case 'GB':
    default:
      return 'Please enter a valid UK postcode.';
  }
};

export const normalizeMemberPostalCode = (
  countryCode: string | null | undefined,
  value: string | null | undefined,
) => {
  const normalized = normalizeRawLocationCode(value);
  if (!normalized) {
    return null;
  }

  switch (normalizeMemberCountryCode(countryCode)) {
    case 'IE': {
      const compact = normalized.replace(/\s+/g, '');
      const matched = compact.match(/^(?:(?:[AC-FHKNPRTV-Y]\d{2})|D6W)([0-9AC-FHKNPRTV-Y]{4})$/i);
      if (!matched) {
        return null;
      }

      return `${compact.slice(0, 3)} ${matched[1]}`.toUpperCase();
    }
    case 'CA': {
      const matched = normalized.match(CA_POSTAL_CODE_PATTERN);
      if (!matched) {
        return null;
      }

      return `${matched[1]} ${matched[2]}`.toUpperCase();
    }
    case 'US': {
      const matched = normalized.match(US_ZIP_CODE_PATTERN);
      if (!matched) {
        return null;
      }

      return matched[2] ? `${matched[1]}-${matched[2]}` : matched[1];
    }
    case 'AU':
    case 'NZ': {
      const matched = normalized.match(AU_NZ_POSTCODE_PATTERN);
      if (!matched) {
        return null;
      }

      return matched[1];
    }
    case 'GB':
    default: {
      const matched = normalized.match(UK_POSTCODE_PATTERN);
      if (!matched) {
        return null;
      }

      return `${matched[1]} ${matched[2]}`.toUpperCase();
    }
  }
};

export const isValidMemberPostalCode = (
  countryCode: string | null | undefined,
  value: string | null | undefined,
) => Boolean(normalizeMemberPostalCode(countryCode, value));

export const detectMemberCountryCodeFromPostalCode = (
  value: string | null | undefined,
): MemberCountryCode | null => {
  const normalized = normalizeRawLocationCode(value);
  if (!normalized) {
    return null;
  }

  if (UK_POSTCODE_PATTERN.test(normalized)) {
    return 'GB';
  }

  if (IE_EIRCODE_PATTERN.test(normalized) || IE_ROUTING_KEY_PATTERN.test(normalized)) {
    return 'IE';
  }

  if (CA_POSTAL_CODE_PATTERN.test(normalized)) {
    return 'CA';
  }

  if (US_ZIP_CODE_PATTERN.test(normalized)) {
    return 'US';
  }

  return null;
};

export const resolveMemberCountryCode = (
  countryCode: string | null | undefined,
  postcode?: string | null | undefined,
): MemberCountryCode | null => {
  if (isMemberCountryCode(countryCode)) {
    return normalizeMemberCountryCode(countryCode);
  }

  return detectMemberCountryCodeFromPostalCode(postcode);
};

export const getPublicLocationCode = (
  value: string | null | undefined,
  countryCode?: string | null,
) => {
  const normalized = normalizeRawLocationCode(value);
  if (!normalized) {
    return null;
  }

  const compact = normalized.replace(/\s+/g, '');
  const resolvedCountry = countryCode
    ? normalizeMemberCountryCode(countryCode)
    : detectMemberCountryCodeFromPostalCode(normalized);

  if (resolvedCountry === 'IE' || IE_ROUTING_KEY_PATTERN.test(compact)) {
    return compact.slice(0, 3) || null;
  }

  if (resolvedCountry === 'CA') {
    const matched = compact.match(/^([ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z])(\d[ABCEGHJ-NPRSTV-Z]\d)$/i);
    return matched?.[1]?.toUpperCase() ?? null;
  }

  if (resolvedCountry === 'US') {
    const matched = compact.match(/^(\d{5})(\d{4})?$/);
    return matched?.[1]?.slice(0, 3) ?? null;
  }

  if (resolvedCountry === 'AU' || resolvedCountry === 'NZ') {
    const matched = compact.match(/^(\d{4})$/);
    return matched?.[1]?.slice(0, 3) ?? null;
  }

  const ukMatch = normalized.match(UK_POSTCODE_PATTERN);
  if (ukMatch?.[1]) {
    return ukMatch[1];
  }

  return normalized.split(' ')[0] ?? null;
};

export const getPublicLocationAreaCode = (
  value: string | null | undefined,
  countryCode?: string | null,
) => {
  const resolvedCountry = resolveMemberCountryCode(countryCode, value);
  if (resolvedCountry !== 'GB') {
    return null;
  }

  const publicCode = getPublicLocationCode(value, resolvedCountry);
  return publicCode?.match(/^[A-Z]+/i)?.[0] ?? null;
};

export const scoreMemberLocalityMatch = (
  viewer: MemberLocationLike | null | undefined,
  candidate: MemberLocationLike | null | undefined,
) => {
  const viewerCountry = resolveMemberCountryCode(viewer?.country_code, viewer?.postcode);
  const candidateCountry = resolveMemberCountryCode(candidate?.country_code, candidate?.postcode);
  const viewerPublicCode = getPublicLocationCode(viewer?.postcode, viewerCountry);
  const candidatePublicCode = getPublicLocationCode(candidate?.postcode, candidateCountry);
  const viewerAreaCode = getPublicLocationAreaCode(viewer?.postcode, viewerCountry);
  const candidateAreaCode = getPublicLocationAreaCode(candidate?.postcode, candidateCountry);

  let score = 0;

  if (viewerCountry && candidateCountry && viewerCountry === candidateCountry) {
    score = 10;
  }

  if (
    viewerCountry &&
    candidateCountry &&
    viewerCountry === candidateCountry &&
    viewerPublicCode &&
    candidatePublicCode &&
    viewerPublicCode === candidatePublicCode
  ) {
    score = Math.max(score, 120);
  }

  if (
    viewerCountry === 'GB' &&
    candidateCountry === 'GB' &&
    viewerAreaCode &&
    candidateAreaCode &&
    viewerAreaCode === candidateAreaCode
  ) {
    score = Math.max(score, 40);
  }

  return score;
};

export const orderMembersByLocality = <T extends MemberLocationLike>(
  members: T[],
  viewer: MemberLocationLike | null | undefined,
) =>
  [...members].sort((left, right) => {
    const scoreDiff = scoreMemberLocalityMatch(viewer, right) - scoreMemberLocalityMatch(viewer, left);
    if (scoreDiff !== 0) {
      return scoreDiff;
    }

    const rightHasLocation = Boolean(right.postcode);
    const leftHasLocation = Boolean(left.postcode);
    if (rightHasLocation !== leftHasLocation) {
      return rightHasLocation ? 1 : -1;
    }

    return (left.display_name ?? '').localeCompare(right.display_name ?? '', 'en-GB', {
      sensitivity: 'base',
    });
  });
